import { Action, MoveNotesAction, MoveSlidesAction, noEffect, noop, UndoableAction } from "../actions";
import { CreateShapeAction, MoveShapeAction, ResizeShapeAction } from "../actions/slides";
import { EPSILON } from "../constants";
import { BoundsType } from "../services/shape-service";

export interface UndoHistoryState {
    readonly future: ReadonlyArray<UndoableAction>,
    readonly past: ReadonlyArray<UndoableAction>,
}

const initialState: UndoHistoryState = {
    future: [],
    past: [],
};


export default function (state: UndoHistoryState = initialState, action: Action): UndoHistoryState {
    switch (action.type) {

        case 'UNDO': {
            const n = state.past.length;
            if (n === 0) {
                return state;
            }
            const current = state.past[n - 1];
            return {
                ...state,
                past: state.past.slice(0, n - 1),
                future: [...state.future, current],
            };
        }

        case 'REDO': {
            const n = state.future.length;
            if (n === 0) {
                return state;
            }
            const current = state.future[n - 1];
            return {
                ...state,
                past: [...state.past, current],
                future: state.future.slice(0, n - 1),
            }
        }

        case 'START_NOTE_DRAGGING': {
            const moveAction = {
                type: 'MOVE_NOTE',
                uuid: action.uuid,
                startX: action.noteX,
                startY: action.noteY,
                endX: action.noteX,
                endY: action.noteY,
                redoEffect: noEffect,
                undoEffect: noEffect,
            } as MoveNotesAction;
            return {
                ...state,
                past: [...state.past, moveAction],
                future: [],
            }
        }

        case 'NOTE_DRAGGING': {
            const lastAction = state.past[state.past.length - 1];
            if (lastAction.type !== 'MOVE_NOTE') {
                return state;
            }
            const moveAction = {
                ...lastAction,
                endX: action.endX,
                endY: action.endY,
                redoEffect: action.redoEffect,
                undoEffect: action.undoEffect,
            };
            return {
                ...state,
                past: [...state.past.slice(0, -1), moveAction],
                future: [],
            }
        }

        case 'STOP_NOTE_DRAGGING': {
            const lastAction = state.past[state.past.length - 1];
            if (lastAction.type !== 'MOVE_NOTE') {
                return state;
            }

            if (Math.abs(action.endX - lastAction.startX) < EPSILON && Math.abs(action.endY - lastAction.startY) < EPSILON) {
                return {
                    ...state,
                    past: [...state.past.slice(0, -1)],
                    future: [],
                }
            }

            const moveAction = {
                ...lastAction,
                endX: action.endX,
                endY: action.endY,
                undoEffect: action.undoEffect,
                redoEffect: action.redoEffect,
            };
            return {
                ...state,
                past: [...state.past.slice(0, -1), moveAction],
                future: [],
            }
        }

        case 'START_SHAPE_DRAGGING': {
            const moveAction = {
                type: 'MOVE_SHAPE',
                slideUuid: action.slideUuid,
                shapeUuid: action.shapeUuid,
                startX: action.shapeX,
                startY: action.shapeY,
                endX: action.shapeX,
                endY: action.shapeY,
                redoEffect: noEffect,
                undoEffect: noEffect,
            } as MoveShapeAction;
            return {
                ...state,
                past: [...state.past, moveAction],
                future: [],
            }
        }

        case 'SHAPE_DRAGGING': {
            const lastAction = state.past[state.past.length - 1];
            if (lastAction.type !== 'MOVE_SHAPE') {
                return state;
            }
            const moveAction = {
                ...lastAction,
                endX: action.endX,
                endY: action.endY,
                redoEffect: action.redoEffect,
                undoEffect: action.undoEffect,
            };
            return {
                ...state,
                past: [...state.past.slice(0, -1), moveAction],
                future: [],
            }
        }

        case 'STOP_SHAPE_DRAGGING': {
            const lastAction = state.past[state.past.length - 1];
            if (lastAction.type !== 'MOVE_SHAPE') {
                return state;
            }

            if (Math.abs(action.endX - lastAction.startX) < EPSILON && Math.abs(action.endY - lastAction.startY) < EPSILON) {
                return {
                    ...state,
                    past: [...state.past.slice(0, -1)],
                    future: [],
                }
            }

            const moveAction = {
                ...lastAction,
                endX: action.endX,
                endY: action.endY,
                undoEffect: action.undoEffect,
                redoEffect: action.redoEffect,
            };
            return {
                ...state,
                past: [...state.past.slice(0, -1), moveAction],
                future: [],
            }
        }

        case 'START_SLIDE_DRAGGING': {
            const moveAction = {
                type: 'MOVE_SLIDE',
                uuid: action.uuid,
                startX: action.slideX,
                startY: action.slideY,
                endX: action.slideX,
                endY: action.slideY,
                redoEffect: noEffect,
                undoEffect: noEffect,
            } as MoveSlidesAction;
            return {
                ...state,
                past: [...state.past, moveAction],
                future: [],
            }
        }

        case 'SLIDE_DRAGGING': {
            const lastAction = state.past[state.past.length - 1];
            if (lastAction.type !== 'MOVE_SLIDE') {
                return state;
            }
            const moveAction = {
                ...lastAction,
                endX: action.endX,
                endY: action.endY,
                redoEffect: action.redoEffect,
                undoEffect: action.undoEffect,
            };
            return {
                ...state,
                past: [...state.past.slice(0, -1), moveAction],
                future: [],
            }
        }

        case 'STOP_SLIDE_DRAGGING': {
            const lastAction = state.past[state.past.length - 1];
            if (lastAction.type !== 'MOVE_SLIDE') {
                return state;
            }

            if (Math.abs(action.endX - lastAction.startX) < EPSILON && Math.abs(action.endY - lastAction.startY) < EPSILON) {
                return {
                    ...state,
                    past: [...state.past.slice(0, -1)],
                    future: [],
                }
            }

            const moveAction = {
                ...lastAction,
                endX: action.endX,
                endY: action.endY,
                undoEffect: action.undoEffect,
                redoEffect: action.redoEffect,
            };
            return {
                ...state,
                past: [...state.past.slice(0, -1), moveAction],
                future: [],
            }
        }

        case 'START_SELECTOR_DRAGGING': {
            const { slideUuid, shape } = action;
            const points = shape.type === 'FREEFORM'? shape.points : [];
            const boundsType = shape.type === 'TEXT'? shape.boundsType : BoundsType.FIXED;
            const moveAction = {
                type: 'RESIZE_SHAPE',
                slideUuid,
                shapeUuid: shape.uuid,
                startX1: shape.x1,
                startY1: shape.y1,
                startX2: shape.x2,
                startY2: shape.y2,
                endX1: shape.x1,
                endY1: shape.y1,
                endX2: shape.x2,
                endY2: shape.y2,
                startPath: shape.path,
                endPath: shape.path,
                startPoints: points,
                endPoints: points,
                startBoundsType: boundsType,
                redoEffect: noEffect,
                undoEffect: noEffect,
            } as ResizeShapeAction;
            return {
                ...state,
                past: [...state.past, moveAction],
                future: [],
            }
        }

        case 'SELECTOR_DRAGGING': {
            const lastAction = state.past[state.past.length - 1];
            if (lastAction.type !== 'RESIZE_SHAPE') {
                return state;
            }
            const moveAction = {
                ...lastAction,
                endX1: action.endX1,
                endY1: action.endY1,
                endX2: action.endX2,
                endY2: action.endY2,
                endPath: action.endPath,
                endPoints: action.endPoints,
                redoEffect: action.redoEffect,
                undoEffect: action.undoEffect,
            };
            return {
                ...state,
                past: [...state.past.slice(0, -1), moveAction],
                future: [],
            }
        }

        case 'STOP_SELECTOR_DRAGGING': {
            const lastAction = state.past[state.past.length - 1];
            if (lastAction.type !== 'RESIZE_SHAPE') {
                return state;
            }

            if (Math.abs(action.endX1 - lastAction.startX1) < EPSILON && Math.abs(action.endY1 - lastAction.startY1) < EPSILON
                && Math.abs(action.endX2 - lastAction.startX2) < EPSILON && Math.abs(action.endY2 - lastAction.startY2) < EPSILON) {
                return {
                    ...state,
                    past: [...state.past.slice(0, -1)],
                    future: [],
                }
            }

            const moveAction = {
                ...lastAction,
                endX1: action.endX1,
                endY1: action.endY1,
                endX2: action.endX2,
                endY2: action.endY2,
                endPath: action.endPath,
                endPoints: action.endPoints,
                undoEffect: action.undoEffect,
                redoEffect: action.redoEffect,
            };
            return {
                ...state,
                past: [...state.past.slice(0, -1), moveAction],
                future: [],
            }
        }

        case 'START_SHAPE_SKETCHING': {
            const createAction = {
                type: 'CREATE_SHAPE',
                slideUuid: action.slideUuid,
                shape: action.shape,
                undoEffect: noEffect,
                redoEffect: noEffect,
            } as CreateShapeAction;
            return {
                ...state,
                past: [...state.past, createAction],
                future: [],
            }
        }

        case 'SHAPE_SKETCHING':
        case 'STOP_SHAPE_SKETCHING': {
            const lastAction = state.past[state.past.length - 1];
            if (lastAction.type !== 'CREATE_SHAPE') {
                return state;
            }
            let shape = {
                ...lastAction.shape,
                x1: action.x1,
                y1: action.y1,
                x2: action.x2,
                y2: action.y2,
                path: action.path,
            };
            if (shape.type === 'FREEFORM') {
                shape = { ...shape, points: action.points };
            }
            const createAction = {
                ...lastAction,
                shape,
                redoEffect: action.redoEffect,
                undoEffect: action.undoEffect,
            };
            return {
                ...state,
                past: [...state.past.slice(0, -1), createAction],
                future: [],
            }
        }

        case 'SET_TEMPLATE_ID':
            return action.newTemplateId === action.oldTemplateId ? state : // used during initialization
                {
                    ...state,
                    past: [...state.past, action],
                    future: [],
                };


        case 'SETUP_STORYBOARD':
        case 'SET_PROCESS_STEP':
        case 'CREATE_NOTE':
        case 'UPDATE_NOTE':
        case 'DELETE_NOTE':
        case 'CREATE_SLIDE':
        case 'DELETE_SLIDE':
        case 'UPDATE_TEXT_SHAPE':
        case 'DELETE_SHAPE':
        case 'SET_GOALS':
        case 'SET_MAIN_MESSAGE':
        case 'SET_AUDIENCE':
        case 'SET_TEXT_FIELD':
            return {
                ...state,
                past: [...state.past, action],
                future: [],
            };

        case 'SET_PRESENTATION':
            return {
                ...state,
                past: [],
                future: [],
            };

        default:
            return state;
    }
}


export const isUndoable = (state: UndoHistoryState): boolean => state.past.length > 0;
export const isRedoable = (state: UndoHistoryState): boolean => state.future.length > 0;
export const getUndoAction = (state: UndoHistoryState): UndoableAction => isUndoable(state) ? state.past[state.past.length - 1] : noop();
export const getRedoAction = (state: UndoHistoryState): UndoableAction => isRedoable(state) ? state.future[state.future.length - 1] : noop();



// WEBPACK FOOTER //
// ./src/reducers/undo-history.ts